import type { TablePaginationConfig } from "antd/es/table";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { DEFAULT_PAGE_SIZE } from "../constants";

export function usePagination(options?: {
  pageSize?: number;
  updateRoutePath?: boolean;
}) {
  const opts = options || { pageSize: DEFAULT_PAGE_SIZE };
  const router = useRouter();
  const [page, setPage] = useState(
    opts.updateRoutePath ? Number(router.query.page) || 1 : 1
  );
  const [pageSize, setPageSize] = useState(
    opts.updateRoutePath
      ? Number(router.query.pageSize) || DEFAULT_PAGE_SIZE
      : opts.pageSize
  );

  const onPaginationChange = useCallback(
    ({ current, pageSize }: { current?: number; pageSize?: number }) => {
      current && setPage(current);
      pageSize && setPageSize(pageSize);

      if (options?.updateRoutePath) {
        router.push({
          pathname: router.pathname,
          query: {
            ...router.query,
            page: current,
            pageSize,
          },
        });
      }
    },
    [options?.updateRoutePath, router]
  );

  return {
    page,
    pageSize,
    pagination: defaultPagination,
    onPaginationChange,
    innerPagination: defaultInnerPagination,
    paginationConfig: defaultPagination({
      page,
      pageSize,
    }),
  };
}

export function defaultInnerPagination({
  pageSize = 15,
  totalCount,
}: {
  pageSize?: number;
  totalCount?: number;
}): TablePaginationConfig {
  return {
    pageSize,
    showSizeChanger: false,
    hideOnSinglePage: true,
    total: totalCount,
    position: ["bottomLeft"],
  };
}

export function defaultPagination({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  totalCount,
  ...config
}: {
  page?: number;
  pageSize?: number;
  totalCount?: number;
} & TablePaginationConfig): TablePaginationConfig {
  return {
    showSizeChanger: true,
    defaultCurrent: 1,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    current: page,
    pageSize: pageSize,
    pageSizeOptions: ["25", "50", "100", "500", "1000"],
    total: totalCount,
    responsive: true,
    showTotal: (total) => (
      <FormattedMessage
        id="list.total"
        defaultMessage="total"
        values={{ total }}
      />
    ),
    ...config,
  };
}
